import { Cpu, ShieldCheck, Settings2, Gauge, ServerCog, LifeBuoy } from "lucide-react";
import Link from "@/components/ui/RouterLink";
import { pageSeo } from "@/config/seo";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { PageHero } from "@/components/ui/PageHero";
import { SectionEyebrow } from "@/components/ui/SectionEyebrow";
import { FeatureCard } from "@/components/ui/FeatureCard";
import { ProductCard } from "@/components/ui/ProductCard";
import { dedicatedPlans } from "@/data/products";

const seo = pageSeo(
  "Dedicated Servers — Infrastructure for Demanding Workloads",
  "Run applications, databases, platforms and digital services on server resources dedicated to your organisation."
);

const benefits = [
  { icon: Cpu, title: "Dedicated Resources", body: "The full server is reserved for your organisation — no shared tenants." },
  { icon: Settings2, title: "Greater Infrastructure Control", body: "Configure the environment around your application's exact requirements." },
  { icon: ServerCog, title: "Custom Configurations", body: "Hardware and software configured to match your specific workload." },
  { icon: Gauge, title: "Suitable for Intensive Workloads", body: "Built for applications, databases and platforms with demanding resource needs." },
  { icon: ShieldCheck, title: "Flexible Operating-System Choices", body: "Select the OS environment your applications are built to run on." },
  { icon: LifeBuoy, title: "Optional Managed Services", body: "Add managed support where you'd rather we handle the maintenance layer." },
];

export function DedicatedServersPage() {
  useDocumentMeta(seo.title, seo.description);

  return (
    <>
      <PageHero
        eyebrow="Dedicated Servers"
        title="Dedicated Infrastructure for Demanding Workloads"
        description="Run applications, databases, platforms and digital services on server resources dedicated to your organisation."
        primaryCta={{ label: "View Dedicated Servers", href: "#plans" }}
        secondaryCta={{ label: "Request Custom Configuration", href: "/contact" }}
      />

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((b) => (
            <FeatureCard key={b.title} {...b} />
          ))}
        </div>
      </section>

      <section id="plans" className="border-y border-border bg-navy-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <SectionEyebrow>Server Plans</SectionEyebrow>
            <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
              Dedicated Server Options
            </h2>
            <p className="mt-4 text-sm text-text-secondary">
              Specifications shown below are placeholders — full details, available locations and
              pricing are confirmed in the billing portal.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {dedicatedPlans.map((plan) => (
              <ProductCard key={plan.id} product={plan} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-20 text-center lg:px-8">
        <h2 className="font-display text-2xl font-semibold text-text-primary sm:text-3xl">
          Need a Configuration That Is Not Listed?
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-text-secondary">
          Tell us about your workload, application, expected traffic, storage requirements and
          preferred environment.
        </p>
        <div className="mt-7">
          <Link
            href="/contact"
            className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110"
          >
            Request a Custom Server
          </Link>
        </div>
      </section>
    </>
  );
}
